import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { TimecardSpec, ContractInfo } from '../../objects/timecard-spec';
import { Contract } from '../../objects/contract';
import { TimesheetSheetsService } from '../google-sheets/timesheet-sheets.service';

/**
 * One sign-off on a submission. Same `{approver, approvedAt, name}` shape the
 * timesheets side uses (unified spec §"approvals[] is canonical audit"), and on
 * cbhi timecards `name` is currently just the approver's email.
 *
 * It records only the APPROVER stage — reviewer sign-off, returns and recalls
 * never appear here. Prefer `stateHistory` when present; this is the fallback.
 */
export interface TimecardApproval {
  approver?: string;
  name?: string;
  approvedAt?: string;
}

/**
 * One lifecycle event on a submission, written by the backend's `runTransition`
 * on every state change — submit, review, approve, reject (return), recall,
 * reopen — with the acting user, the note they left, and the timestamp.
 *
 * This is the full story of a period; `approvals[]` is the approve-only subset.
 */
export interface TimecardStateEvent {
  action?: string;      // submit | review | approve | reject | recall | reopen
  actorEmail?: string;
  fromStatus?: string;
  toStatus?: string;
  note?: string | null; // return reason / recall reason; null or '' when none
  at?: string;          // ISO instant
}

/** One row from the admin submissions list (the timecard approval queue). */
export interface TimecardSubmissionRow {
  principalEmail: string;
  yearMonth: string;
  /** Period sort key. The backend emits both; `yearMonth` is the legacy alias. */
  periodKey?: string;
  // cbhi timecards vocabulary: draft | submitted | under_review | approved | rejected
  // (approved is TERMINAL — timecards have no payment stage, so no pending_payment/paid).
  status: string;
  // Emails eligible to act on this submission right now (any one may act). Replaces
  // the old assignedTo/assignedRole, which don't exist on the cbhi timecards row.
  currentAssignees?: string[];
  totalHours?: number;
  totalAmount?: number;
  /** Approve-stage sign-offs. See {@link TimecardApproval}. */
  approvals?: TimecardApproval[];
  /** Full lifecycle audit. See {@link TimecardStateEvent}. */
  stateHistory?: TimecardStateEvent[];
  submittedAt?: string;
  updatedAt?: string;
}

/**
 * One row from the assigned-submissions endpoint. The backend resolves the
 * approvalChain server-side (incl. alternate approvers) and denormalizes the
 * HCP name from the spec, so no client-side join is needed.
 */
export interface TimecardAssignedRow {
  principalEmail: string;
  /** Period sort key (§10 rename of yearMonth). Normalized in the service to
   *  tolerate a backend that still emits `yearMonth`. */
  periodKey: string;
  status: string;
  hcpName?: string;
  firstName?: string;
  lastName?: string;
  totalHours?: number;
  totalAmount?: number | null;
  /** Emails eligible to act on this submission now (any one may act). Replaces the
   *  old assignedTo/assignedRole, which don't exist on the cbhi timecards row — never
   *  use a single "assignee" to test membership. */
  currentAssignees?: string[];
  submittedAt?: string;
  updatedAt?: string;
  /**
   * Contracts this timecard's HCP is assigned to, denormalized from the spec by
   * the backend. Optional: present once the assigned-submissions handler ships
   * it; until then the queue falls back to a client-side spec-list join.
   */
  contracts?: ContractInfo[];
}

/** Server-bucketed response from GET /submissions/assigned/{email} (all months).
 *  2 tabs only — timecards have no payment stage (§4), so no forPayment. */
export interface TimecardAssignedSubmissions {
  userEmail: string;
  counts: { forReview: number; forApproval: number };
  forReview: TimecardAssignedRow[];
  forApproval: TimecardAssignedRow[];
}

/**
 * TimecardsService — calls the timecards backend at URL_TIMECARDS_BASE.
 *
 * Mirrors {@link TimesheetsService} method-for-method and uses the same
 * `(environment as any).URL_*` access pattern. The auth interceptor attaches
 * the Cognito ACCESS token to any URL starting with URL_TIMECARDS_BASE.
 *
 * Note: the backend exists but the TimecardSpec endpoints aren't deployed yet.
 * Calls will return 404 until the timecards-side build ships them. UI surfaces
 * the failure via the existing snackbar pattern; no changes needed here when
 * the endpoints land.
 */
@Injectable({ providedIn: 'root' })
export class TimecardsService {
  private http = inject(HttpClient);
  private timesheetSheetsService = inject(TimesheetSheetsService);

  /**
   * PBJ job-code catalog. Sourced from the same Google Sheet read as the
   * timesheet editor — both editors must show the same canonical list. The
   * method lives on TimesheetSheetsService because that's where the
   * `timesheet`-tab access already is; we delegate rather than duplicate.
   */
  getPbjJobCodes() {
    return firstValueFrom(this.timesheetSheetsService.getPbjJobCodes());
  }

  /**
   * Denormalize a Contract → ContractInfo for a TimecardSpec snapshot.
   *
   * `contractedParty*` is always pulled from `directVendor` (the contract's
   * primary signatory), regardless of which side of the activation filter
   * matched the contract.
   *
   * Returns null if the contract has no contractId — callers should treat
   * this as an abortable error (we can't ship a half-built ContractInfo).
   */
  buildContractInfo(contract: Contract | null | undefined): ContractInfo | null {
    if (!contract || !contract.contractId) return null;

    const direct = this.unwrapVendor(contract.directVendor);
    const directNum = direct?.vendorNum ?? '';
    const directName = direct?.name
      ?? contract.directVendorFriendlyName
      ?? '';

    const info: ContractInfo = {
      contractId: contract.contractId ?? '',
      contractName: contract.nameOfContract ?? '',
      contractedPartyNum: directNum,
      contractedPartyName: directName
    };

    if (direct?.partyType) info.contractedPartyType = direct.partyType;
    if (direct?.suffix) info.contractedPartySuffix = direct.suffix;
    if (direct?.firstName) info.contractedPartyFirstName = direct.firstName;
    if (direct?.lastName) info.contractedPartyLastName = direct.lastName;

    const sites = (contract as any).sites;
    if (Array.isArray(sites)) {
      const siteIds = sites
        .map((s: any) => typeof s === 'string' ? s : (s?.siteNum || s?.id || ''))
        .filter((s: string) => s);
      if (siteIds.length > 0) info.contractSites = siteIds;
    }

    if (contract.expirationDate) info.contractExpirationDate = contract.expirationDate;

    return info;
  }

  /**
   * Reads the flat vendor info off whichever wrapper shape the cache contains.
   * Hydrated parties on contracts may be wrapped (`{ vendorData: {...}, vendorNum }`)
   * or flat — handle both.
   */
  private unwrapVendor(v: any): any | null {
    if (!v || typeof v === 'string') return null;
    if (v.vendorData && typeof v.vendorData === 'object') {
      return { vendorNum: v.vendorNum ?? v.vendorData.vendorNum, ...v.vendorData };
    }
    return v;
  }

  /** Create or update a TimecardSpec. */
  writeTimecardSpec(spec: TimecardSpec): Observable<TimecardSpec> {
    const url = (environment as any).URL_TIMECARD_SPECS_WRITE;
    return this.http.post<TimecardSpec>(url, spec);
  }

  /** Look up a TimecardSpec by its specId. */
  getTimecardSpec(specId: string): Observable<TimecardSpec | null> {
    const url = (environment as any).URL_TIMECARD_SPECS_GET + specId;
    return this.http.get<any>(url).pipe(
      map(response => response?.spec ?? response ?? null)
    );
  }

  /** Look up the TimecardSpec linked to a given child party. */
  getTimecardSpecByParty(partyNum: string): Observable<TimecardSpec | null> {
    const url = (environment as any).URL_TIMECARD_SPECS_BY_PARTY + partyNum;
    return this.http.get<any>(url).pipe(
      map(response => response?.spec ?? response ?? null),
      catchError(() => of(null))
    );
  }

  /**
   * Paginated / list-all read of TimecardSpecs. Mirrors
   * TimesheetsService.getAllTimesheetSpecs() — POST with empty body so the
   * backend can later support filters in the body without an URL change.
   * Defensive unwrap handles either { specs: [...] } or a bare array shape.
   * Errors fail soft so the list page renders an empty grid rather than crashing.
   *
   * TODO (debug logging): trim the verbose request/response logs once the
   * list-endpoint contract is stable.
   */
  listTimecardSpecs(): Observable<TimecardSpec[]> {
    const url = (environment as any).URL_TIMECARD_SPECS_LIST;
    console.log('[timecard-spec-list] → POST', url);
    return this.http.post<any>(url, {}).pipe(
      tap(response => {
        const responseType = Array.isArray(response) ? 'array'
          : (response && typeof response === 'object' ? `object keys=[${Object.keys(response).join(',')}]` : typeof response);
        const arr = this.unwrapSpecArray(response);
        console.log('[timecard-spec-list] ← 200', { url, responseType, count: arr.length, response });
      }),
      map(response => this.unwrapSpecArray(response)),
      catchError((err: HttpErrorResponse) => {
        console.error('[timecard-spec-list] Failed', {
          status: err.status,
          statusText: err.statusText,
          url: err.url ?? url,
          method: 'POST',
          responseBody: err.error,
          message: err.message
        });
        return of([] as TimecardSpec[]);
      })
    );
  }

  /**
   * Defensive list-response unwrap. The timecards backend currently returns
   * `{ items, lastEvaluatedKey }` (DynamoDB pagination envelope). Other backends
   * in this codebase use `{ specs: [...] }` (timesheets) or a bare array.
   * Always returns an array so the caller's `.map()` can't crash.
   *
   * TODO: cursor pagination — when item counts grow past one page, follow
   * `lastEvaluatedKey` and accumulate. Out of scope for the demo (current
   * usage is one page max).
   */
  private unwrapSpecArray(response: any): TimecardSpec[] {
    if (Array.isArray(response)) return response as TimecardSpec[];
    if (Array.isArray(response?.items)) return response.items as TimecardSpec[];
    if (Array.isArray(response?.specs)) return response.specs as TimecardSpec[];
    return [];
  }

  // ── Approval queue (My Work reviewer/approver views) ──────────────────────

  /**
   * The caller's timecard submissions needing action, server-bucketed across ALL
   * months (no month axis — an action queue must never hide work behind a date filter).
   * Self-only: `email` must equal the caller's own Cognito email or the backend 403s.
   *
   * The backend owns the approvalChain resolution (including alternate approvers, which
   * `assignedTo` alone would miss) and denormalizes the HCP name — so this fully replaces
   * the old `/admin/submissions?month=` + client-side spec join. Fails soft to empty buckets.
   */
  getAssignedTimecardSubmissions(email: string): Observable<TimecardAssignedSubmissions> {
    const empty: TimecardAssignedSubmissions = {
      userEmail: email, counts: { forReview: 0, forApproval: 0 }, forReview: [], forApproval: []
    };
    const base = (environment as any).URL_TIMECARD_SUBMISSIONS_ASSIGNED;
    if (!base || !email) return of(empty);
    const url = base + encodeURIComponent(email);
    // Normalize each row's period sort key — tolerate periodKey OR legacy yearMonth.
    const normRow = (r: any): TimecardAssignedRow => ({ ...r, periodKey: r?.periodKey ?? r?.yearMonth ?? '' });
    return this.http.get<any>(url).pipe(
      map(res => {
        const forReview: TimecardAssignedRow[] = (Array.isArray(res?.forReview) ? res.forReview : []).map(normRow);
        const forApproval: TimecardAssignedRow[] = (Array.isArray(res?.forApproval) ? res.forApproval : []).map(normRow);
        return {
          userEmail: res?.userEmail ?? res?.email ?? email,
          counts: {
            forReview: res?.counts?.forReview ?? forReview.length,
            forApproval: res?.counts?.forApproval ?? forApproval.length
          },
          forReview, forApproval
        } as TimecardAssignedSubmissions;
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('[TimecardsService] getAssignedTimecardSubmissions failed', err?.status, err?.message);
        return of(empty);
      })
    );
  }

  /**
   * System-administrator ONLY: ALL in-flight timecard submissions across every HCP
   * and month, bucketed by stage — the admin counterpart to getAssignedTimecardSubmissions.
   * Backend gates to role 'System Administrator' (403 otherwise) and returns the SAME
   * shape as the assigned endpoint, so the queue reuses the same rendering. Fails soft
   * to empty buckets (e.g. 403 for a non-system-admin, or endpoint not yet deployed).
   */
  getActiveAdminSubmissions(): Observable<TimecardAssignedSubmissions> {
    const url = (environment as any).URL_TIMECARD_ADMIN_SUBMISSIONS_ACTIVE;
    return this.http.get<any>(url).pipe(
      map(res => ({
        userEmail: res?.userEmail ?? '',
        counts: {
          forReview: res?.counts?.forReview ?? (Array.isArray(res?.forReview) ? res.forReview.length : 0),
          forApproval: res?.counts?.forApproval ?? (Array.isArray(res?.forApproval) ? res.forApproval.length : 0),
        },
        forReview: Array.isArray(res?.forReview) ? res.forReview : [],
        forApproval: Array.isArray(res?.forApproval) ? res.forApproval : [],
      } as TimecardAssignedSubmissions)),
      catchError((err: HttpErrorResponse) => {
        console.error('[timecard-admin-active] Failed', err?.status, err?.message);
        return of({ userEmail: '', counts: { forReview: 0, forApproval: 0 }, forReview: [], forApproval: [] } as TimecardAssignedSubmissions);
      })
    );
  }

  /**
   * Every submission for ONE HCP, all periods — the Timecard Ledger's status read.
   *
   * A base-table Query on `principalEmail` (the table's partition key), paged to
   * completion server-side and returned newest period first. It replaces one
   * `?month=` call per period, each of which returned every OTHER HCP's
   * submissions for that month only to have them filtered out in the browser.
   *
   * Admin route (authorizer only). The principal-scoped `GET /submissions?principalEmail=`
   * is NOT usable here: it runs requireAccessToPrincipal, which grants
   * self/proxy/named-submitter only, so it 403s for the HC admin viewing a spec.
   *
   * Rows carry `periodKey` and a `yearMonth` alias; normalized here to periodKey.
   * The handler returns the WHOLE submission item (verified against the deployed
   * `CbhiTimecardsStack-Submissions` bundle, 2026-09-13), so `approvals[]` and
   * `stateHistory[]` come back with it — that is what the ledger's Approval
   * History card reads, and it is why no new endpoint was needed for it.
   * Fails soft to an empty list — a missing status degrades the ledger's Status
   * column to the entry-derived value, which it already labels as such, rather
   * than failing the whole screen.
   */
  getSubmissionsForPrincipal(principalEmail: string): Observable<TimecardSubmissionRow[]> {
    const base = (environment as any).URL_TIMECARD_ADMIN_SUBMISSIONS_HCP;
    if (!base || !principalEmail) return of([] as TimecardSubmissionRow[]);
    const url = base + encodeURIComponent(principalEmail);
    return this.http.get<any>(url).pipe(
      map(res => {
        const items: any[] = Array.isArray(res?.items) ? res.items
          : Array.isArray(res?.submissions) ? res.submissions
          : Array.isArray(res) ? res : [];
        return items.map(r => ({ ...r, yearMonth: r?.periodKey ?? r?.yearMonth ?? '' })) as TimecardSubmissionRow[];
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('[timecard-submissions-by-hcp] Failed', err?.status, err?.message);
        return of([] as TimecardSubmissionRow[]);
      })
    );
  }

  /**
   * Admin list of timecard submissions for one month, across all HCPs.
   *
   * ⚠️ NO CALLERS as of 2026-09-13. Kept because it answers a question the sparse
   * active index cannot — "every submission in period X, including terminal ones" —
   * off the non-sparse byPeriodStatus index. If you are reaching for it to build a
   * per-HCP view, use getSubmissionsForPrincipal instead: calling this once per
   * period is the N+1 the ledger was just moved off.
   */
  getAdminSubmissions(month: string): Observable<TimecardSubmissionRow[]> {
    const url = (environment as any).URL_TIMECARD_ADMIN_SUBMISSIONS + '?month=' + encodeURIComponent(month);
    return this.http.get<any>(url).pipe(
      map(res => (Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : [])) as TimecardSubmissionRow[]),
      catchError((err: HttpErrorResponse) => {
        console.error('[timecard-admin-submissions] Failed', err.status, err.message);
        return of([] as TimecardSubmissionRow[]);
      })
    );
  }

  /**
   * The actual timecard entries for one HCP. `month` ('YYYY-MM') narrows to a
   * single period; OMIT it to get every entry the HCP has ever logged, which is
   * what the Timecard Ledger reads (the base table is keyed by principalEmail,
   * so it's one query either way).
   *
   * This is an ADMIN route — gated by the Cognito authorizer only, NOT by the
   * self/proxy/named-submitter check that guards `GET /submissions?principalEmail=`.
   * That matters: the person opening a Timecard Spec is an HC admin who is
   * normally none of those three, so the principal-scoped route would 403.
   *
   * Errors PROPAGATE — the ledger has to tell "no timecards yet" apart from "the
   * fetch failed". The approval queue already catches per-call inside its forkJoin.
   */
  getHcpTimecards(principalEmail: string, month?: string): Observable<any> {
    const url = (environment as any).URL_TIMECARD_ADMIN_HCP
      + encodeURIComponent(principalEmail)
      + (month ? '?month=' + encodeURIComponent(month) : '');
    return this.http.get<any>(url);
  }

  /**
   * Reviewer action on a submission (status must be `submitted`, canonical §3).
   * POST /api/{org}/approvals/review  body: { principalEmail, periodKey, action, reason? }
   *   approve → status `under_review` (routes to the approver)
   *   return  → status `rejected` (back to the HCP to edit + resubmit)
   *
   * Auth: actor derived from the ID token (NO changedBy). principalEmail required.
   * Backend authorizes per-actor (403), guards state (409), fires ALL emails.
   *
   * Return-note key is `reason` (confirmed from the deployed handler — NOT `note`,
   * despite spec §2's wording). The `note` UI label flows in via this `reason` field.
   */
  reviewSubmission(principalEmail: string, periodKey: string, action: 'approve' | 'return', reason = ''): Observable<any> {
    const url = (environment as any).URL_TIMECARD_APPROVALS_REVIEW;
    return this.http.post<any>(url, { principalEmail, periodKey, action, reason });
  }

  /**
   * Approver action on a submission (status must be `under_review`, canonical §3).
   * POST /api/{org}/approvals/approve  body: { principalEmail, periodKey, action, reason? }
   *   approve → status `approved` (TERMINAL — timecards have no payment stage, §4)
   *   return  → status `rejected` (back to the HCP)
   *
   * `send_back_to_reviewer` is RETIRED in the unified model. Any primary or alternate
   * approver may act; one approval needed. Same auth/guards/email-ownership + the same
   * `reason` return-note key as reviewSubmission.
   */
  approveSubmission(principalEmail: string, periodKey: string, action: 'approve' | 'return', reason = ''): Observable<any> {
    const url = (environment as any).URL_TIMECARD_APPROVALS_APPROVE;
    return this.http.post<any>(url, { principalEmail, periodKey, action, reason });
  }

  // TODO: implement when needed.
  // replaceTimecardSpec() { ... reads URL_TIMECARD_SPECS_REPLACE ... }
}
